// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import CustomSearch from "../../../../components/custom-search/custom-search";
import SubscriptionService from "../../subscription.service";
import { formatNumber } from "../../../../utils/helper/functions";

const Pending: React.FC = (): ReactElement => {
  let searchTimer = null;
  const subscriptionService = new SubscriptionService();
  const navigate = useNavigate();
  const loadingState = useSelector((state: any) => state?.loading);
  const [pendingRequestsList, setPendingRequestsList] = useState<any[]>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchPendingListData();
  }, [searchWord, sortField, sortOrder, filters]);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      width: 170,
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <span>{record?.portfolio?.account?.activeKyc?.nameEn}</span>
      ),
    },
    {
      title: "Client account",
      dataIndex: "accountCode",
      key: "accountCode",
      width: 130,
      sorter: true,
      render: (_, record) => (
        <Link
          to={`/home/business-account/${record?.portfolio?.account?.id}/${record?.portfolio?.account?.activeKycId}/details`}
        >
          {record?.portfolio?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Wealth manager",
      dataIndex: "wealthManager",
      key: "wealthManager",
      render: (_, record) =>
        record?.portfolio?.account?.wealthManager?.ae1 || "-",
    },
    {
      title: "Funds name",
      dataIndex: "fundName",
      key: "fundName",
      fixed: "left",
      render: (_, record) => <span>{record?.mutualFund?.NAME}</span>,
    },
    {
      title: "Portfolio number",
      dataIndex: "portfolioCode",
      key: "portfolioCode",
      width: 170,
      sorter: true,
      render: (_, record) => (
        <span>{record?.portfolio?.accountCode || "-"}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "subscriptionAmount",
      key: "subscriptionAmount",
      render: (text) => <span>{text ? formatNumber(text) : ""}</span>,
    },
    {
      title: "Fees",
      dataIndex: "subscriptionFees",
      key: "subscriptionFees",
      width: 120,
      render: (text) => <span>{formatNumber(text)}</span>,
    },
    {
      title: "Currency",
      dataIndex: "subscriptionCurrency",
      key: "subscriptionCurrency",
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "souce",
      render: (text) =>
        text === "Portal" ? (
          <ReactSVG src={"/images/monitor.svg"} />
        ) : (
          <ReactSVG src={"/images/mobile.svg"} />
        ),
    },
    {
      title: "Request Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      sorter: true,
      render: (text) => (
        <span className="text-gray-500">
          {moment(text).format("MMM DD YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <ReactSVG
          src={"/images/take-action.svg"}
          onClick={() => {
            navigate(`/subscription/take-action/${record?.id}`);
          }}
        />
      ),
    },
  ];

  const fetchPendingListData = async (page = 1): Promise<void> => {
    const data = await subscriptionService.getSubscriptionListData(
      ["Pending"],
      page,
      searchWord,
      sortField,
      sortOrder,
      filters
    );

    if (data !== undefined) {
      setResponseDate(data?.data);
      setPendingRequestsList(data?.data?.subscriptions);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilters(JSON.stringify(filters?.status));
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? pendingRequestsList?.length : ""} Pending requests
        </p>
        <div className="flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>
      {pendingRequestsList?.length ? (
        <CustomTable
          columns={columns}
          data={pendingRequestsList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchPendingListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default Pending;
